import { TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import React from "react";
import { BaseProps } from "../../../shared/props";
import { isMobileViewport, useViewport } from "../../../styles";
import { RichText, RichTextProps } from "../../RichText/RichText";
import { ShimmerProgressView } from "../../Shimmer/ShimmerProgressView/ShimmerProgressView";
import { Stack, StackItem } from "../../Stacks/Stack/Stack";
import { Text } from "../../Text/Text";
import { BodyTextVariant } from "../Feature.types";
import { FeatureCallToAction, FeatureCallToActionProps } from "../FeatureCallToAction/FeatureCallToAction";
import { FeatureContentShimmer } from "./FeatureContentShimmer/FeatureContentShimmer";
import { FeatureHeading, FeatureHeadingProps } from "./FeatureHeading";

export type BodyTextType = string | RichTextProps | readonly (string | RichTextProps)[];

export interface FeatureContentProps extends BaseProps {
    /**
     * Feature Heading
     */
    readonly featureHeading?: FeatureHeadingProps;
    /**
     * Feature content text.
     */
    readonly bodyText?: BodyTextType;
    /**
     * Style (size/weight/color) for the body text.
     */
    readonly bodyTextVariant?: BodyTextVariant;
    /**
     * Feature CTA. Input is an array as there can be more than 1 CTAs.
     * CTA choices are button, hyperlink or toggle.
     * Order of the CTAs in the array will be the rendering order.
     */
    readonly ctaProps?: readonly FeatureCallToActionProps[];
    /**
     * Contains operation data.
     */
    readonly operationData?: TwoUIProgressViewOperationData;
}

export const FeatureContent: React.FC<FeatureContentProps> = ({
    featureHeading,
    bodyText,
    bodyTextVariant = "bodyPrimaryText",
    ctaProps,
    operationData
}) => {

    let header: JSX.Element | undefined;
    if (featureHeading) {
        header = <FeatureHeading {...featureHeading} />;
    }

    let content: JSX.Element | undefined;
    if (typeof bodyText === "string") {
        content = (
            <Text variant={bodyTextVariant}>
                {bodyText}
            </Text>
        );
    } else if (Array.isArray(bodyText)) {
        const textComponents = (bodyText.map((text: string | RichTextProps, key: number) => {
            if (typeof text === "string") {
                return (
                    <Text key={key} variant={bodyTextVariant}>
                        {text}
                    </Text>
                );
            } else {
                return (<RichText key={key} {...text} />);
            }
        }));
        content = <Stack childrenGap={2}>{textComponents}</Stack>;
    } else if (bodyText) {
        content = (<RichText {...bodyText} />);
    }

    const marginStyle = "0 0 12px";

    const viewportType = useViewport();
    const isMobile = isMobileViewport(viewportType);

    const contentBlock = (
        <Stack grow={true}>
            {header && (
                <StackItem margin={marginStyle}>
                    {header}
                </StackItem>
            )}
            <StackItem margin={marginStyle}>
                {content}
            </StackItem>
            <Stack horizontal={!isMobile} childrenGap={12} verticalAlign="baseline">
                {ctaProps && ctaProps.map((ctaProp, index) => <FeatureCallToAction key={index} {...ctaProp} />)}
            </Stack>
        </Stack>
    );

    if (operationData) {
        return (
            <ShimmerProgressView
                operationData={operationData}
                firstLoadView={<FeatureContentShimmer />}
                finishedView={contentBlock}
                failedView={undefined}
            />
        );
    } else {
        return contentBlock;
    }

};
